import { BaseRecipeStore } from './domain/base-store.js';
import type { RecipeStore, RevisionBackend, StoreContext } from './domain/store.js';
import { MemoryDocumentAdapter } from './memory/adapter.js';
import type { DocumentHandle } from './yjs/document.js';
import { createDocument } from './yjs/document.js';

export interface CreateStoreOptions {
  readonly context: StoreContext;
  readonly revisions?: RevisionBackend;
}

/** Fast, dependency-free store for UI tests and previews. */
export function createMemoryStore(options: CreateStoreOptions): RecipeStore {
  return new BaseRecipeStore({ adapter: new MemoryDocumentAdapter(), ...options });
}

export interface CreateYjsStoreResult {
  readonly store: RecipeStore;
  readonly handle: DocumentHandle;
}

/**
 * The production store. Migrations run on open, before the store reads anything, so a
 * document written by an older client is brought up to shape before the UI ever sees it.
 */
export function createYjsStore(
  options: CreateStoreOptions & { handle?: DocumentHandle },
): CreateYjsStoreResult {
  const handle = options.handle ?? createDocument(`household:${options.context.householdId}`);
  // A viewer must not write, and a migration is a write, so their document is left as-is
  // and read through the same converters that tolerate older shapes.
  if (options.context.role !== 'viewer') handle.migrate(options.context);
  const adapter = handle.createAdapter();
  const store = new BaseRecipeStore({ adapter, context: options.context, revisions: options.revisions });
  return { store, handle };
}
